import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ModalBody } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, xcss } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { ObservableSpinner } from '@confluence/infinite-scroll';
import { useTeamData } from '@confluence/permissions-principals/entry-points/useTeamData.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { PRINCIPAL_TYPE } from '../constants.ts';

import {
	ConfluenceGroupUsageType,
	SitePermissionType as ContentAccessSitePermissionType,
} from './__types__/AccessDetailsDialogContentAccessDetailsQuery.ts';
import type { AccessDetailsDialogContentAccessDetailsQuery as AccessDetailsDialogContentAccessDetailsQuery$data } from './__types__/AccessDetailsDialogContentAccessDetailsQuery.ts';
import type { AccessDetailsDialogSpaceAccessDetailsQuery as AccessDetailsDialogSpaceAccessDetailsQuery$data } from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery.ts';
import {
	ConfluencePrincipalType as SpaceAccessPrincipalType,
	SitePermissionType as SpaceAccessSitePermissionType,
} from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery.ts';
import { AccessDetailsPrincipal } from './AccessDetailsPrincipal.tsx';

type TeamDataItems = NonNullable<
	NonNullable<ReturnType<typeof useTeamData>['teamData']>['teamsV2']
>;

const specificAccessContainer = xcss({
	paddingBottom: 'space.250',
});

const i18n = defineMessages({
	countOfSubjects: {
		id: 'space-access-details.access-details-dialog.count-of-subjects',
		defaultMessage:
			'{countOfSubjects, plural,=0 {No suggestions found.} one {# suggestion found.} other {# suggestions found.}}',
		description: 'Accessible announcement for screen readers when the user types in the search box',
	},
});

type AccessDetailsDialogBodyProps = {
	spaceInfo?: AccessDetailsDialogSpaceAccessDetailsQuery$data;
	contentInfo?: AccessDetailsDialogContentAccessDetailsQuery$data;
	isLoading: boolean;
	hasInheritedRestrictions: boolean;
	handleSpaceAccessDetailsFetchMore: (_?: string | null) => void;
	handleContentAccessDetailsFetchMore: (_?: string | null) => void;
};

type AccessSubject = {
	id?: string | null;
	type: PRINCIPAL_TYPE;
	isGuest: boolean;
	displayName?: string | null;
	secondLine?: string | null;
	avatarUrl?: string;
	verified?: boolean;
	teamTypeName?: string;
};

function parseSpaceInfoSubjects(
	spaceInfo?: AccessDetailsDialogSpaceAccessDetailsQuery$data,
	extraTeamData?: TeamDataItems,
): AccessSubject[] {
	return (spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes || []).map((item) => {
		if (item?.subjectKey?.principalType === SpaceAccessPrincipalType.USER) {
			return {
				id: item?.subjectKey?.id,
				type: PRINCIPAL_TYPE.USER,
				displayName: item?.subjectKey?.displayName,
				avatarUrl: item?.subjectKey?.user?.profilePicture?.path || '',
				secondLine: item?.subjectKey?.user?.email,
				isGuest: item?.subjectKey?.user?.permissionType === SpaceAccessSitePermissionType.EXTERNAL,
			};
		} else if (
			item?.subjectKey?.principalType === SpaceAccessPrincipalType.GROUP &&
			item.subjectKey?.group?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION &&
			fg('confluence_frontend_perms_exp_teams_as_principals')
		) {
			const itemId = item?.subjectKey?.id;
			const matchingTeam = extraTeamData?.find((team) =>
				team?.id && team?.id?.toString().lastIndexOf('/') >= 0
					? team?.id?.toString().substring(team?.id?.toString().lastIndexOf('/') + 1) === itemId
					: false,
			);
			return {
				id: itemId,
				type: PRINCIPAL_TYPE.TEAM,
				displayName: item?.subjectKey.displayName,
				isGuest:
					item?.subjectKey.group?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
				avatarUrl: matchingTeam?.largeAvatarImageUrl ?? undefined,
				verified: matchingTeam?.type?.verified ?? undefined,
				teamTypeName: matchingTeam?.type?.name ?? undefined,
			};
		}

		// else not a user, so it must be a group
		return {
			id: item?.subjectKey?.id,
			type: PRINCIPAL_TYPE.GROUP,
			displayName: item?.subjectKey?.displayName,
			isGuest: item?.subjectKey?.group?.permissionType === SpaceAccessSitePermissionType.EXTERNAL,
		};
	});
}

function parseContentInfoSubjects(
	contentInfo?: AccessDetailsDialogContentAccessDetailsQuery$data,
	teamData?: TeamDataItems,
): AccessSubject[] {
	return (
		contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes || []
	).map((item) => {
		if (item?.type === PRINCIPAL_TYPE.USER) {
			return {
				id: item?.id,
				type: PRINCIPAL_TYPE.USER,
				displayName: item?.displayName,
				isGuest: item?.user?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
				avatarUrl: item?.user?.profilePicture?.path || '',
				secondLine: item?.user?.email,
			};
		} else if (
			item?.type === PRINCIPAL_TYPE.GROUP &&
			item.group?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION
		) {
			const matchingTeam = teamData?.find((team) =>
				team?.id && team?.id?.toString().lastIndexOf('/') >= 0
					? team?.id?.toString().substring(team?.id?.toString().lastIndexOf('/') + 1) === item.id
					: false,
			);
			return {
				id: item?.id,
				type: PRINCIPAL_TYPE.TEAM,
				displayName: item?.displayName,
				isGuest: item?.group?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
				avatarUrl: matchingTeam?.largeAvatarImageUrl ?? undefined,
				verified: matchingTeam?.type?.verified ?? undefined,
				teamTypeName: matchingTeam?.type?.name ?? undefined,
			};
		}

		// else not a user, so it must be a group
		return {
			id: item?.id,
			type: PRINCIPAL_TYPE.GROUP,
			displayName: item?.displayName,
			isGuest: item?.group?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
		};
	});
}

export const AccessDetailsDialogBody: FC<AccessDetailsDialogBodyProps> = ({
	spaceInfo,
	contentInfo,
	hasInheritedRestrictions,
	handleContentAccessDetailsFetchMore,
	handleSpaceAccessDetailsFetchMore,
	isLoading,
}) => {
	const intl = useIntl();
	const [subjectListCount, setSubjectListCount] = useState<number | null>(null);
	const { cloudId } = useSessionData();

	const teamIds = useMemo(() => {
		const nodes = spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes || [];
		const ids: string[] = [];
		for (const item of nodes) {
			if (
				item?.subjectKey?.group &&
				item.subjectKey.group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION &&
				item.subjectKey.group.id
			) {
				ids.push(item.subjectKey.group.id);
			}
		}
		return ids;
	}, [spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes]);
	const { teamData: extraTeamData, refetch: refetchExtraTeamData } = useTeamData(cloudId, teamIds);
	/*
          Removing bold highlighting of search match sections due to:
          https://asecurityteam.atlassian.net/browse/VULN-1084090

          Ticket for bringing that feature back:
          https://product-fabric.atlassian.net/browse/CPE-1437
     */

	const subjectList = hasInheritedRestrictions
		? parseContentInfoSubjects(contentInfo, extraTeamData?.teamsV2 ?? undefined)
		: parseSpaceInfoSubjects(spaceInfo, extraTeamData?.teamsV2 ?? undefined);

	const hasNextPage = hasInheritedRestrictions
		? Boolean(
				contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.pageInfo
					?.hasNextPage,
			)
		: Boolean(spaceInfo?.spacePermissions?.subjectsWithPermissions?.pageInfo?.hasNextPage);

	const nextPageCursor = hasInheritedRestrictions
		? contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.pageInfo?.endCursor
		: spaceInfo?.spacePermissions?.subjectsWithPermissions?.pageInfo?.endCursor;

	const handleFetchMore = hasInheritedRestrictions
		? handleContentAccessDetailsFetchMore
		: handleSpaceAccessDetailsFetchMore;

	useEffect(() => {
		// Delay to ensure Assistive Technology registers the live region before update
		// Also delay so screen readers announce the count last
		const timer = setTimeout(() => {
			setSubjectListCount(subjectList.length);
		}, 5000);
		return () => clearTimeout(timer);
	}, [subjectList]);

	const teamIdsFromSpaceInfo = useMemo(() => {
		const nodes = spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes || [];
		const ids: string[] = [];
		for (const item of nodes) {
			if (
				item?.subjectKey?.group &&
				item.subjectKey.group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION &&
				item.subjectKey.group.id
			) {
				ids.push(item.subjectKey.group.id);
			}
		}
		return ids;
	}, [spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes]);

	const teamIdsFromContentInfo = useMemo(() => {
		const nodes =
			contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes || [];
		const ids: string[] = [];
		for (const item of nodes) {
			if (
				item?.group &&
				item.group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION &&
				item.group.id
			) {
				ids.push(item.group.id);
			}
		}
		return ids;
	}, [contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes]);

	useEffect(() => {
		if (teamIdsFromSpaceInfo.length > 0) {
			void refetchExtraTeamData({ teamIds: teamIdsFromSpaceInfo, siteId: cloudId });
		}
	}, [teamIdsFromSpaceInfo, cloudId, refetchExtraTeamData]);

	useEffect(() => {
		if (teamIdsFromContentInfo.length > 0) {
			void refetchExtraTeamData({ teamIds: teamIdsFromContentInfo, siteId: cloudId });
		}
	}, [teamIdsFromContentInfo, cloudId, refetchExtraTeamData]);

	return (
		<ModalBody>
			<Stack
				space="space.150"
				alignInline="start"
				grow="fill"
				as="div"
				xcss={specificAccessContainer}
			>
				{subjectList.map((item) => (
					<AccessDetailsPrincipal {...item} key={item.id} />
				))}
				<VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
					{intl.formatMessage(i18n.countOfSubjects, { countOfSubjects: subjectListCount })}
				</VisuallyHidden>
			</Stack>
			{!isLoading && hasNextPage && (
				<ObservableSpinner
					onScrollTo={() => {
						handleFetchMore(nextPageCursor);
					}}
					cursor={nextPageCursor}
					testId="access-details-spinner"
					observerOptions={{
						root: null,
						rootMargin: '0px',
						threshold: 0.05,
					}}
				/>
			)}
		</ModalBody>
	);
};
